import { LoaderFunction, useLoaderData, defer } from "react-router-dom";
import { getActiveAssets } from "../../../common/api/decimal/methods/license.ts";
import { DecimalResult } from "../../../common/api/decimal/utils.ts";
import { ExploreAugmentedAsset } from "../../../common/api/decimal/common-types/manageAssets.ts";
import { getAssetsByVbid } from "../../../common/api/bridge/methods.ts";
import { getRecommendations } from "../../../common/api/decimal/methods/recommendations.ts";
import { loadBrandOnce } from "../../root/rootLoader.ts";

type RecommendationsResponse = ReturnType<typeof getRecommendations>;

interface LoaderData {
  recentBooks: ReturnType<typeof getActiveAssets>;
  recentRecommendations: RecommendationsResponse;
}

const getRecentAssets = () => {
  return new Promise<DecimalResult<ExploreAugmentedAsset[]>>(
    (resolve, reject) => {
      getActiveAssets(1, 10)
        .then((licenseResponse) => {
          if (!licenseResponse.ok) {
            reject(new Error("Could not load recent assets"));
          }
          const vbids = licenseResponse.ok
            ? licenseResponse.data?.map((asset) => asset.manageAsset.vbid)
            : [];
          if (vbids?.length) {
            return getAssetsByVbid(vbids).then((bridgeResponse) => {
              const exploreLookup = bridgeResponse.content.reduce<
                Record<string, VST.Bridge.Asset>
              >((acc, curr) => {
                acc[curr.vbid] = curr;
                return acc;
              }, {});
              if (licenseResponse.data) {
                for (const asset of licenseResponse.data) {
                  asset.explore = exploreLookup[asset.manageAsset.vbid];
                }
              }
              resolve(licenseResponse);
            });
          } else {
            resolve(licenseResponse);
          }
        })
        .catch((error) => reject(error));
    }
  );
};

const getRecommendationsIfNeeded = async (): RecommendationsResponse => {
  const brand = await loadBrandOnce();

  if (brand.Recommendations.enabled) {
    return getRecommendations();
  } else {
    return { ok: false, meta: {} };
  }
};

export const dashboardLoader: LoaderFunction = () => {
  return defer({
    recentBooks: getRecentAssets(),
    recentRecommendations: getRecommendationsIfNeeded(),
  });
};

function useDashboardLoaderData() {
  return useLoaderData() as LoaderData;
}

export function useRecentBooksLoaderData() {
  return useDashboardLoaderData().recentBooks;
}

export function useRecentRecommendationsLoaderData() {
  return useDashboardLoaderData().recentRecommendations;
}
